import React, { useState } from "react";
import { bugReportsApi } from "./api";

const COLORS = {
  indigo: "#6366F1",
  text: "#F8F7FF",
  muted: "rgba(148,163,184,0.7)",
  border: "rgba(255,255,255,0.07)",
  red: "#EF4444",
};

function HelpModal({ title, onClose, children, maxWidth = 520 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(8, 12, 24, 0.45)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "rgba(13, 18, 36, 0.72)",
          backdropFilter: "blur(20px) saturate(160%)",
          WebkitBackdropFilter: "blur(20px) saturate(160%)",
          border: `1px solid rgba(255, 255, 255, 0.1)`,
          borderRadius: 20,
          padding: "28px 32px",
          width: "100%",
          maxWidth,
          maxHeight: "85vh",
          overflowY: "auto",
          animation: "modalPop 0.35s ease",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.35)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 22, fontWeight: 600 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: COLORS.muted,
              fontSize: 22,
              cursor: "pointer",
              lineHeight: 1,
              padding: 4,
            }}
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function HelpSection({ title, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, marginBottom: 8 }}>{title}</h3>
      <div style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65 }}>{children}</div>
    </div>
  );
}

function HelpLink({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "none",
        border: "none",
        padding: 0,
        color: COLORS.indigo,
        cursor: "pointer",
        fontSize: "inherit",
        textDecoration: "underline",
        textUnderlineOffset: 2,
      }}
    >
      {children}
    </button>
  );
}

function TopicCard({ title, description, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        padding: "14px 16px",
        marginBottom: 10,
        borderRadius: 12,
        border: `1px solid ${COLORS.border}`,
        background: "rgba(255,255,255,0.03)",
        cursor: "pointer",
        transition: "background 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, color: COLORS.text, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: COLORS.muted, lineHeight: 1.5 }}>{description}</div>
    </button>
  );
}

export const FAQ_ITEMS = [
  {
    q: "How does AI Startup Validator work?",
    a: "Describe your startup idea in a few sentences and click Validate. Our AI analyzes it across five VC dimensions — market size, timing, competition, monetization, and defensibility — and returns a verdict with bull case, bear case, and a killer insight in about 8 seconds.",
  },
  {
    q: "Is startup idea validation free?",
    a: "Yes. Every account includes 3 free validations with full access to all five score dimensions, bull and bear analysis, and the killer insight. No credit card is required to start.",
  },
  {
    q: "What do the verdicts mean?",
    a: "Fund It means the idea shows strong potential across most dimensions. Needs Work means there is promise but significant gaps to address. Hard Pass means the idea faces major structural challenges — consider pivoting or rethinking the approach.",
  },
  {
    q: "What is Layer 2 — Idea Development?",
    a: "After a Needs Work or Hard Pass verdict, Layer 2 unlocks five AI tools: Pivot Suggestor, ICP Generator, Competitor Mapper, Landing Page Generator, and Pitch Deck Builder. Each tool uses your validation scores and killer insight as context.",
  },
  {
    q: "What happens if my idea gets Needs Work?",
    a: "You get the full analysis plus access to Layer 2 development tools. Pivot Suggestor is available on the free plan; ICP Generator requires Starter; Competitor Mapper, Landing Page Generator, and Pitch Deck Builder require Pro.",
  },
  {
    q: "What dimensions does the AI score?",
    a: "Market size, timing, competition, monetization, and defensibility — the same lenses a venture capitalist uses to evaluate early-stage startups.",
  },
  {
    q: "How do credits and plans work?",
    a: "Free accounts get 3 validations total. Starter includes 50 validations per month. Pro and Team plans offer unlimited validations. Credits are deducted when a validation completes successfully — failed requests do not consume credits.",
  },
  {
    q: "Can I save and revisit past validations?",
    a: "Yes, when signed in. Each successful validation is saved to your sidebar history. You can star important sessions and filter them in the library view.",
  },
  {
    q: "Can I sign in with Google?",
    a: "Yes, if Google sign-in is enabled on the server. Use the Google button on the login modal. Your validation history syncs to your account.",
  },
  {
    q: "Is my startup idea kept private?",
    a: "Ideas are stored in your account history and sent to AI providers for analysis. Do not submit confidential information you are not comfortable sharing. See our Privacy Policy for details.",
  },
  {
    q: "Can I use this before applying to YC or raising funding?",
    a: "Absolutely. Founders use AI Startup Validator to stress-test ideas before YC applications, investor meetings, and major product pivots.",
  },
  {
    q: "How is this different from asking ChatGPT?",
    a: "Unlike a generic chatbot, we use a structured VC scoring framework with calibrated dimensions, consistent verdicts, and actionable killer insights tailored for founders.",
  },
  {
    q: "What if validation fails or errors?",
    a: "Check your internet connection and try again. If you are out of credits, upgrade your plan. API errors usually resolve within a minute — credits are restored if a validation fails after being deducted.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Contact support@aistartupvalidator.com or manage billing through your Stripe customer portal if available. Paid plans renew automatically until cancelled.",
  },
];

function FaqAccordionItem({ item, open, onToggle }) {
  return (
    <div style={{ borderBottom: `1px solid ${COLORS.border}` }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          width: "100%",
          padding: "14px 0",
          border: "none",
          background: "transparent",
          color: COLORS.text,
          fontSize: 14,
          fontWeight: 500,
          textAlign: "left",
          cursor: "pointer",
        }}
      >
        <span>{item.q}</span>
        <span style={{ color: COLORS.muted, fontSize: 18, lineHeight: 1, flexShrink: 0 }}>{open ? "−" : "+"}</span>
      </button>
      {open && (
        <p style={{ margin: "0 0 14px", fontSize: 14, color: COLORS.muted, lineHeight: 1.65 }}>{item.a}</p>
      )}
    </div>
  );
}

export function HelpPanel({ onClose, onNavigate }) {
  const go = (panel) => () => {
    onClose();
    onNavigate?.(panel);
  };

  return (
    <HelpModal title="Help center" onClose={onClose} maxWidth={560}>
      <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65, marginBottom: 20 }}>
        Guides and answers for using AI Startup Validator.
      </p>

      <HelpSection title="Getting started">
        <p style={{ margin: "0 0 10px" }}>
          Paste your idea, click Validate, and read your VC-style verdict in seconds.
        </p>
      </HelpSection>

      <TopicCard
        title="FAQ"
        description="Common questions about scoring, credits, accounts, and privacy."
        onClick={go("faq")}
      />
      <TopicCard
        title="Keyboard shortcuts"
        description="Speed up your workflow with keyboard commands."
        onClick={go("shortcuts")}
      />
      <TopicCard
        title="Release notes"
        description="See what's new in the latest versions."
        onClick={go("release-notes")}
      />

      <HelpSection title="Quick start">
        <ol style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 8 }}>Sign up free — you get 3 validations to start.</li>
          <li style={{ marginBottom: 8 }}>Describe your startup idea in plain language (2–4 sentences works best).</li>
          <li style={{ marginBottom: 8 }}>Click <strong style={{ color: COLORS.text }}>Validate</strong> or press ⌘/Ctrl + Enter.</li>
          <li>Review scores, bull/bear cases, and the killer insight. Iterate on your idea and validate again.</li>
        </ol>
      </HelpSection>

      <HelpSection title="Understanding your results">
        <p style={{ margin: "0 0 10px" }}>
          Each validation scores five dimensions from 0–100. The overall verdict combines these into Fund It, Needs Work,
          or Hard Pass. Use the bull case to sharpen your pitch and the bear case to prepare for investor objections.
        </p>
      </HelpSection>

      <HelpSection title="Credits & plans">
        <p style={{ margin: "0 0 10px" }}>
          Free: 3 validations · Starter: 50/mo · Pro: unlimited · Team: unlimited (5 seats). Upgrade from the sidebar
          profile menu or when you run out of credits.
        </p>
      </HelpSection>

      <HelpSection title="Legal & support">
        <p style={{ margin: 0 }}>
          Read our <HelpLink onClick={go("terms")}>Terms of Service</HelpLink> and{" "}
          <HelpLink onClick={go("privacy")}>Privacy Policy</HelpLink>. Found a bug?{" "}
          <HelpLink onClick={go("report-bug")}>Report it here</HelpLink>. Email{" "}
          <a href="mailto:support@aistartupvalidator.com" style={{ color: COLORS.indigo }}>
            support@aistartupvalidator.com
          </a>
          .
        </p>
      </HelpSection>
    </HelpModal>
  );
}

export function FaqPanel({ onClose, onNavigate }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <HelpModal title="FAQ" onClose={onClose} maxWidth={560}>
      <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65, marginBottom: 20 }}>
        Everything you need to know about validating startup ideas with AI.
      </p>

      {FAQ_ITEMS.map((item, i) => (
        <FaqAccordionItem
          key={item.q}
          item={item}
          open={openIndex === i}
          onToggle={() => setOpenIndex(openIndex === i ? -1 : i)}
        />
      ))}

      <p style={{ fontSize: 13, color: COLORS.muted, marginTop: 20, marginBottom: 0 }}>
        Still stuck? Visit the{" "}
        <HelpLink
          onClick={() => {
            onClose();
            onNavigate?.("help");
          }}
        >
          Help center
        </HelpLink>{" "}
        or email{" "}
        <a href="mailto:support@aistartupvalidator.com" style={{ color: COLORS.indigo }}>
          support@aistartupvalidator.com
        </a>
        .
      </p>
    </HelpModal>
  );
}

const RELEASE_NOTES = [
  {
    version: "1.4",
    date: "June 2026",
    items: [
      "ChatGPT-style profile menu with Help submenu",
      "In-app Help center, FAQ, Terms, and Privacy panels",
      "Google sign-in and Stripe billing support",
    ],
  },
  {
    version: "1.3",
    date: "May 2026",
    items: [
      "User accounts with validation history",
      "Starter, Pro, and Team subscription plans",
      "Starred sessions and library filters",
    ],
  },
  {
    version: "1.2",
    date: "April 2026",
    items: [
      "Idea development panel with follow-up suggestions",
      "Landing page with pricing and FAQ",
      "Improved score visualization and verdict animation",
    ],
  },
];

export function ReleaseNotesPanel({ onClose }) {
  return (
    <HelpModal title="Release notes" onClose={onClose} maxWidth={520}>
      {RELEASE_NOTES.map((release) => (
        <div key={release.version} style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
            <h3 style={{ fontFamily: "Space Grotesk, sans-serif", fontSize: 16, fontWeight: 600, margin: 0 }}>
              v{release.version}
            </h3>
            <span style={{ fontSize: 13, color: COLORS.muted }}>{release.date}</span>
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, color: COLORS.muted, fontSize: 14, lineHeight: 1.75 }}>
            {release.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ))}
    </HelpModal>
  );
}

export function KeyboardShortcutsPanel({ onClose }) {
  const shortcuts = [
    { action: "Validate idea", keys: "⌘ / Ctrl + Enter" },
    { action: "Close panel / modal", keys: "Esc" },
    { action: "New validation session", keys: "Click + New in sidebar" },
  ];

  return (
    <HelpModal title="Keyboard shortcuts" onClose={onClose} maxWidth={480}>
      <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65, marginBottom: 20 }}>
        Speed up your workflow with these shortcuts.
      </p>
      {shortcuts.map((s) => (
        <div
          key={s.action}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
            padding: "12px 0",
            borderBottom: `1px solid ${COLORS.border}`,
            fontSize: 14,
          }}
        >
          <span style={{ color: COLORS.muted }}>{s.action}</span>
          <span
            style={{
              color: COLORS.text,
              fontFamily: "ui-monospace, monospace",
              fontSize: 13,
              background: "rgba(255,255,255,0.06)",
              padding: "4px 10px",
              borderRadius: 6,
              border: `1px solid ${COLORS.border}`,
            }}
          >
            {s.keys}
          </span>
        </div>
      ))}
    </HelpModal>
  );
}

export function TermsPanel({ onClose }) {
  return (
    <HelpModal title="Terms of Service" onClose={onClose} maxWidth={560}>
      <p style={{ fontSize: 13, color: COLORS.muted, marginBottom: 20 }}>
        <em>Last updated: June 19, 2026</em>
      </p>

      <HelpSection title="Agreement">
        By using AI Startup Validator at aistartupvalidator.com, you agree to these terms.
      </HelpSection>

      <HelpSection title="Service description">
        AI Startup Validator provides AI-generated analysis of startup ideas for informational purposes. Results are not
        investment advice, legal advice, or a guarantee of business success.
      </HelpSection>

      <HelpSection title="Accounts">
        You are responsible for your account credentials and all activity under your account. You must provide accurate
        information when registering.
      </HelpSection>

      <HelpSection title="Acceptable use">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 6 }}>Do not abuse the API or attempt to circumvent usage limits.</li>
          <li style={{ marginBottom: 6 }}>Do not submit illegal content or content that violates third-party rights.</li>
          <li>Do not reverse-engineer, scrape, or resell the service without permission.</li>
        </ul>
      </HelpSection>

      <HelpSection title="Subscriptions and billing">
        Paid plans renew automatically unless cancelled. Refunds are handled per our 7-day money-back guarantee policy.
        Payments are processed by Stripe.
      </HelpSection>

      <HelpSection title="Intellectual property">
        You retain ownership of ideas you submit. We retain ownership of the platform, branding, and AI analysis
        framework. AI-generated reports are licensed to you for personal and business use.
      </HelpSection>

      <HelpSection title="Disclaimer">
        The service is provided &quot;as is&quot; without warranties. AI outputs may be inaccurate or incomplete. Always
        conduct your own due diligence before making business or investment decisions.
      </HelpSection>

      <HelpSection title="Contact">
        <a href="mailto:support@aistartupvalidator.com" style={{ color: COLORS.indigo }}>
          support@aistartupvalidator.com
        </a>
      </HelpSection>
    </HelpModal>
  );
}

export function PrivacyPanel({ onClose }) {
  return (
    <HelpModal title="Privacy Policy" onClose={onClose} maxWidth={560}>
      <p style={{ fontSize: 13, color: COLORS.muted, marginBottom: 20 }}>
        <em>Last updated: June 19, 2026</em>
      </p>

      <HelpSection title="Overview">
        AI Startup Validator operates aistartupvalidator.com. This policy explains what data we collect and how we use
        it.
      </HelpSection>

      <HelpSection title="Information we collect">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 6 }}>
            <strong style={{ color: COLORS.text }}>Account data:</strong> name and email when you sign up (including via
            Google OAuth).
          </li>
          <li style={{ marginBottom: 6 }}>
            <strong style={{ color: COLORS.text }}>Startup ideas:</strong> text you submit for validation, stored in
            your account history.
          </li>
          <li style={{ marginBottom: 6 }}>
            <strong style={{ color: COLORS.text }}>Payment data:</strong> processed by Stripe; we do not store full card
            numbers.
          </li>
          <li>
            <strong style={{ color: COLORS.text }}>Usage data:</strong> basic logs for security, abuse prevention, and
            service reliability.
          </li>
        </ul>
      </HelpSection>

      <HelpSection title="How we use your data">
        <ul style={{ margin: 0, paddingLeft: 20 }}>
          <li style={{ marginBottom: 6 }}>To provide AI validation results and save your validation history.</li>
          <li style={{ marginBottom: 6 }}>To manage subscriptions, billing, and customer support.</li>
          <li>To improve product quality and prevent fraud or abuse.</li>
        </ul>
      </HelpSection>

      <HelpSection title="AI processing">
        Startup idea text is sent to third-party AI providers to generate analysis. Do not submit confidential
        information you are not comfortable sharing with AI services.
      </HelpSection>

      <HelpSection title="Data retention">
        We retain account and validation data while your account is active. You may request deletion by contacting{" "}
        <a href="mailto:support@aistartupvalidator.com" style={{ color: COLORS.indigo }}>
          support@aistartupvalidator.com
        </a>
        .
      </HelpSection>

      <HelpSection title="Your rights (GDPR)">
        If you are in the EU/EEA, you may request access, correction, deletion, or portability of your personal data.
      </HelpSection>

      <HelpSection title="Cookies">
        We use essential cookies and local storage for authentication and session management. We do not use third-party
        advertising cookies.
      </HelpSection>
    </HelpModal>
  );
}

export function ReportBugPanel({ onClose, user, onLogin }) {
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);

  if (!user) {
    return (
      <HelpModal title="Report a bug" onClose={onClose} maxWidth={520}>
        <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65, marginBottom: 24 }}>
          Bug reports can only be submitted by signed-in users so we can follow up with you.
        </p>
        <button
          type="button"
          onClick={() => {
            onClose();
            onLogin?.();
          }}
          style={{
            width: "100%",
            padding: "14px 20px",
            borderRadius: 10,
            border: "none",
            background: `linear-gradient(135deg, ${COLORS.indigo}, #7C3AED)`,
            color: "#fff",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Log in to report a bug
        </button>
      </HelpModal>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await bugReportsApi.submit({
        description,
        steps,
        pageUrl: window.location.href,
      });
      setSent(true);
    } catch (err) {
      setError(err.message || "Failed to submit bug report");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 14px",
    marginBottom: 12,
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    background: "rgba(255,255,255,0.04)",
    color: COLORS.text,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    resize: "vertical",
  };

  return (
    <HelpModal title="Report a bug" onClose={onClose} maxWidth={520}>
      <p style={{ fontSize: 14, color: COLORS.muted, lineHeight: 1.65, marginBottom: 20 }}>
        Describe the issue and our team will review it in the admin console. Reporting as{" "}
        <strong style={{ color: COLORS.text }}>{user.email}</strong>.
      </p>

      {sent ? (
        <p style={{ fontSize: 14, color: COLORS.text, lineHeight: 1.65 }}>
          Thanks — your bug report was submitted. We&apos;ll look into it and may contact you at {user.email}.
        </p>
      ) : (
        <form onSubmit={submit}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>What happened?</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            rows={4}
            placeholder="Describe the bug..."
            style={inputStyle}
          />

          <label style={{ display: "block", fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Steps to reproduce</label>
          <textarea
            value={steps}
            onChange={(e) => setSteps(e.target.value)}
            rows={3}
            placeholder="1. Go to…&#10;2. Click…&#10;3. See error"
            style={inputStyle}
          />

          {error && (
            <p style={{ color: COLORS.red, fontSize: 13, marginBottom: 12 }}>{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px 20px",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg, ${COLORS.indigo}, #7C3AED)`,
              color: "#fff",
              fontSize: 15,
              fontWeight: 600,
              cursor: loading ? "wait" : "pointer",
              marginTop: 4,
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Submitting…" : "Submit bug report"}
          </button>
        </form>
      )}
    </HelpModal>
  );
}
